import { DataViews } from '../../../modules/DataViews';
import { FormatHeight } from '../../../modules/FormatHeight';
import { GetConnectionLookup } from '../../../modules/GetConnectionLookup';
import { GetJobData } from '../../../modules/GetJobData';
import { PickAnAttribute } from '../../../modules/PickAnAttribute';
import calibratePhoto from '../../calibrate-photo/calibrate-photo';

export async function gradeCertification(
  userEmail,
  uid,
  userGroup,
  nodes,
  connections,
  traces,
  jobId,
  keyJobData,
  certFinalizedAttributeName,
  certAttemptsRemaining
) {
  // Get photo and connection data
  const userPhotoDataObj = await GetJobData(jobId, 'photos').then((data) => data.photos || null);
  const connectionLookup = GetConnectionLookup(nodes, connections);

  // Separate out the user and key questions/answers
  const questionNodes = Object.entries(nodes).filter((node) => PickAnAttribute(node[1].attributes, 'node_type') == 'question');
  const answerNodes = Object.values(keyJobData.nodes).filter((node) => PickAnAttribute(node.attributes, 'node_type') == 'question');

  // Firebase info
  let nodeUpdateInfo = {};
  let manualNodeIds = [];

  // Set the starting grade and questions that need to be manually graded to 0
  let [certificationGrade, manualGradingNumber] = [0, 0];

  // Determine Grade if an answer key was found
  if (answerNodes) {
    // Go through the list of questions (with user work and answers)
    for (let i = 0; i < questionNodes.length; i++) {
      // Define commonly used variables
      const answerNode = answerNodes[i];
      const nodeId = questionNodes[i][0];
      const nodeData = questionNodes[i][1];
      const nodeAttributes = nodeData.attributes;
      const question = PickAnAttribute(nodeAttributes, 'question').trim();

      // Set an overall pass to be set per question
      let pass = false;
      let manualGradingFlag = false;

      // If the answer directs the user to star a photo
      if (question.includes('Star')) {
        Object.entries(nodeData.photos).forEach((photo) => {
          // If the starred photo in the answer key is also starred in the user's job
          if (answerNode.photos[photo[0]].association === 'main' && photo[1].association === 'main') pass = true;
        });
        [nodeUpdateInfo, certificationGrade] = trackGrade(pass, nodeUpdateInfo, nodeId, manualGradingFlag, certificationGrade);
        continue;
      }

      // A hardcoded list of the attributes besides the answer attribute. This would need added to if more attributes are added, or if the user adds attributes
      const attributesToFilterOut = [
        'done',
        'node_type',
        'question',
        'scid',
        'certification_note',
        'incorrect_question',
        'needs_manual_grading'
      ];
      const answerAttribute = Object.entries(nodeAttributes).find((attribute) => !attributesToFilterOut.includes(attribute[0]));

      // If there exists an attribute outside of the non-answer attributes (this should only include the answer, but will break if the user added attributes)
      if (answerAttribute) {
        const attributeName = answerAttribute[0];
        const answers = Object.values(answerNode.attributes[attributeName]);
        const userAnswers = Object.values(answerAttribute[1]);
        const singleAnswer = answers[0];
        const singleInput = userAnswers[0];

        // Skip loops if diff number of answers
        if (answers.length !== userAnswers.length) {
          [nodeUpdateInfo, certificationGrade] = trackGrade(pass, nodeUpdateInfo, nodeId, manualGradingFlag, certificationGrade);
          continue;
        }

        // Evaluate specific questions that have a single line input and need additional checking
        if (question === 'Based on the information in the photo below, what is the reason for the violation?') {
          const lowercase = singleInput.toLowerCase();
          if (lowercase.includes('neutral') && lowercase.includes('40') && lowercase.includes('above') && lowercase.includes('proposed'))
            pass = true;
          [nodeUpdateInfo, certificationGrade] = trackGrade(pass, nodeUpdateInfo, nodeId, manualGradingFlag, certificationGrade);
          continue;
        }

        // Check if the key answer is a height (ft inch). If yes, normalize the formats for comparison
        const isFtInch = singleAnswer.match(/[1-9]?[1-9]?'-[1-9]?[1-9]?"/g) ? true : false;
        if (isFtInch) {
          // Check that the answer only has as many digits as the key does (no spamming to pass)
          const maxDigitsInKey = singleAnswer.match(/\d/g).length;
          const digitsInUserAnswer = singleInput.match(/\d/g)?.length;
          if (maxDigitsInKey === digitsInUserAnswer) {
            // Falsy setup to separate out all the 1 or 2 number chunks
            const numbers = singleAnswer.match(/(?<!\d)[1-9]?[1-9]?(?!\d)/g)?.filter((number) => number);
            const userNumbers = singleInput.match(/(?<!\d)[1-9]?[1-9]?(?!\d)/g)?.filter((number) => number);
            const equal =
              userNumbers.length > 0 && numbers.length > 0 && numbers.every((number, numIndex) => number == userNumbers[numIndex]);
            if (equal) pass = true;
          }
          [nodeUpdateInfo, certificationGrade] = trackGrade(pass, nodeUpdateInfo, nodeId, manualGradingFlag, certificationGrade);
          continue;
        }

        // For all other attributes questions, simply check that the key value matches the input
        pass = answers.length > 0 && userAnswers.length > 0 && answers.every((keyValue) => userAnswers.includes(keyValue));
        [nodeUpdateInfo, certificationGrade] = trackGrade(pass, nodeUpdateInfo, nodeId, manualGradingFlag, certificationGrade);
        continue;
      }

      // Certification note switch case - represents questions that have you do work on other poles
      const certNote = PickAnAttribute(nodeAttributes, 'certification_note')?.trim();
      if (certNote) {
        let [scidPhotoData, scids, singleScidData, proposedMarkers, otherPropGuy] = [[], [], [], [], []];
        let [heightOk, mrPass] = [false, false];
        let propDownGuy = {};

        switch (certNote) {
          default:
            manualGradingNumber++;
            manualNodeIds.push(nodeId);
            manualGradingFlag = true;
            break;
          case "Use SCID's 121, 122, and 124 to complete the following task.":
            let noNewAnchorPass = false;
            scids = getScids(['121', '122', '124'], nodes);
            for (let i = 0; i < scids.length; i++) {
              const scidData = scids[i][1];
              const scidId = scids[i][0];
              proposedMarkers = getScidData(scidData, userPhotoDataObj, traces);

              if (!proposedMarkers) continue;

              const newAnchors = connectionLookup[scidId].filter(
                (connection) => PickAnAttribute(connection.toNode?.attributes, 'node_type') === 'new anchor'
              );

              if (PickAnAttribute(scidData.attributes, 'scid') == '122') {
                // Check that the proposed markers on this node meet standards
                noNewAnchorPass = newAnchors.length === 0;
                propDownGuy = proposedMarkers.find((marker) => marker?.guying_type === 'down guy');
                heightOk = FormatHeight(propDownGuy?._measured_height, 'feet-inches') === '26\'-6"';
                mrPass = propDownGuy?.mr_note.includes('Install an aux eye') && propDownGuy?.mr_note.includes('existing anchor rod');
              } else {
                // Check that there are no guying proposed markers on the other two nodes
                const badDownGuys = proposedMarkers.filter((marker) => marker?._marker_context.type === 'guying');
                if (badDownGuys) badDownGuys.forEach((guy) => otherPropGuy.push(guy));
              }
            }
            // Check for pass
            if (propDownGuy && otherPropGuy.length === 0 && heightOk && mrPass && noNewAnchorPass) pass = true;
            [nodeUpdateInfo, certificationGrade] = trackGrade(pass, nodeUpdateInfo, nodeId, manualGradingFlag, certificationGrade);
            break;
          case "Use SCID's 101, 102, and 103 to complete the following task.":
            scids = getScids(['101', '102', '103'], nodes);
            let newAnchorPass = true;
            for (let i = 0; i < scids.length; i++) {
              const scidData = scids[i][1];
              const scidId = scids[i][0];
              proposedMarkers = getScidData(scidData, userPhotoDataObj, traces);

              if (!proposedMarkers) continue;

              const newAnchors = connectionLookup[scidId].filter(
                (connection) => PickAnAttribute(connection.toNode?.attributes, 'node_type') === 'new anchor'
              );

              if (PickAnAttribute(scidData.attributes, 'scid') == '101') {
                // Check that the proposed markers on this node (if any) meet standards
                propDownGuy = proposedMarkers.find((marker) => marker?.guying_type === 'down guy');
                heightOk = FormatHeight(propDownGuy?._measured_height, 'feet-inches') === '21\'-10"';
                const anchor = newAnchors[0];
                if (
                  newAnchors.length !== 1 ||
                  anchor.bearing > 285.6 ||
                  anchor.bearing < 285.5 ||
                  anchor.length > 15.001 ||
                  anchor.length < 14.999
                )
                  newAnchorPass = false;
              } else {
                // Check that there are no guying proposed markers or anchors for the other two nodes
                if (newAnchors.length > 0) newAnchorPass = false;
                const badDownGuys = proposedMarkers.filter((marker) => marker?._marker_context.type === 'guying');
                if (badDownGuys) badDownGuys.forEach((guy) => otherPropGuy.push(guy));
              }
            }
            // Check for pass
            if (propDownGuy && otherPropGuy.length === 0 && heightOk && newAnchorPass) pass = true;
            [nodeUpdateInfo, certificationGrade] = trackGrade(pass, nodeUpdateInfo, nodeId, manualGradingFlag, certificationGrade);
            break;
          case 'Use SCID 115 to complete the following task.':
            singleScidData = getScids(['115'], nodes)[1];
            if (PickAnAttribute(singleScidData.attributes, 'load_case')) pass = true;
            [nodeUpdateInfo, certificationGrade] = trackGrade(pass, nodeUpdateInfo, nodeId, manualGradingFlag, certificationGrade);
            break;
          case 'Use SCIDS 110 and 111 to complete the following task.':
            singleScidData = getScids(['111'], nodes)[1];
            scidPhotoData = getScidPhotoData(singleScidData, userPhotoDataObj);
            scidPhotoData.forEach((photo) => {
              const proposedMarkers = DataViews.help
                .getProposedMarkers(photo.photofirst_data, traces)
                .filter((marker) => marker._marker_context.type === 'wire');
              const existingMarkers = DataViews.help
                .getMarkers(photo.photofirst_data, traces)
                .filter((marker) => marker._marker_context.type === 'wire');
              const scid111Markers = [...proposedMarkers, ...existingMarkers];
              const pointLoaded = scid111Markers.length === 4 && scid111Markers.every((marker) => marker.point_load_end);
              if (pointLoaded) pass = true;
              [nodeUpdateInfo, certificationGrade] = trackGrade(pass, nodeUpdateInfo, nodeId, manualGradingFlag, certificationGrade);
            });
            break;
          case 'Use SCID 108 to complete the following task.':
            singleScidData = getScids(['108'], nodes);
            scidPhotoData = getScidPhotoData(singleScidData[1], userPhotoDataObj);
            const nodeConnections = connectionLookup[singleScidData[0]];
            const midspan = Array(nodeConnections.find((connection) => connection.toScid === '107'));
            scidPhotoData.forEach((photo) => {
              const scid108Markers = DataViews.help
                .getMarkers(photo?.photofirst_data, traces)
                .filter((marker) => marker._marker_context.type === 'wire');
              scid108Markers.forEach((marker) => {
                const midspanMarkers = DataViews.help.getMidspanMarkers(
                  marker,
                  'all',
                  singleScidData[1],
                  nodes,
                  midspan,
                  userPhotoDataObj,
                  traces
                );
                const issueTelco = midspanMarkers.find(
                  (midMarker) => midMarker?.marker?._measured_height < 238 && midMarker?.marker?._measured_height > 236
                );
                if (issueTelco && issueTelco.marker.wire_spec === '16M (7/16) + 2.00" TELCO (7/0)') pass = true;
              });
            });
            [nodeUpdateInfo, certificationGrade] = trackGrade(pass, nodeUpdateInfo, nodeId, manualGradingFlag, certificationGrade);
            break;
        }
        continue;
      }

      // Get data for last question - data from the question node
      // All of these nodes have only one photo, so I use find, but filter (or the getScidPhotoData) would work for multiple photos/main photo
      const nodePhotoKey = nodeData?.photos ? Object.keys(nodeData.photos)[0] : '';

      // Make the answer key's photo data into an array and get only the photos for this node
      const keyDataRef = Object.entries(keyJobData.photos).find((photo) => nodePhotoKey === photo[0]);
      const keyPhotoData = keyDataRef ? keyDataRef[1] : {};
      // Get marker data for the answer key
      const keyMarkers = DataViews.help
        .getMarkers(keyPhotoData?.photofirst_data, keyJobData.traces)
        .filter((marker) => marker._marker_context.type !== 'pole_top');

      // Make the users's photo data into an array and get only the photos for this node
      const userDataRef = Object.entries(userPhotoDataObj).find((photo) => nodePhotoKey === photo[0]);
      const userPhotoData = userDataRef ? userDataRef[1] : {};
      // Get marker data for the certification test
      const userMarkers = DataViews.help
        .getMarkers(userPhotoData?.photofirst_data, traces)
        .filter((marker) => marker._marker_context.type !== 'pole_top');

      // Final types - annotation questions
      switch (question) {
        default:
          manualGradingNumber++;
          manualNodeIds.push(nodeId);
          manualGradingFlag = true;
          break;
        case 'Annotate the CATV Com cable at the correct place in the photo. Use the wire marker input.':
          // 294 is the correct height - 24 ft 6 in
          const diff = Math.abs(294 - userMarkers[0]?._measured_height);
          if (diff <= 1.5) pass = true;
          [nodeUpdateInfo, certificationGrade] = trackGrade(pass, nodeUpdateInfo, nodeId, manualGradingFlag, certificationGrade);
          break;
        case 'Annotate the lowest Telco com cable at the correct place in the photo. Use the wire marker input.':
          const height = userMarkers[0]?._measured_height;
          const heightPass = height <= 260.5 && height >= 253.5 ? true : false;
          const photoId = Object.keys(nodeData.photos)[0];
          const photo = userPhotoDataObj[photoId];
          const photoPixelWidth = (photo.image_width * userMarkers[0]?.pixel_selection[0].percentX) / 100;
          const stickAlignPixelWidth = calibratePhoto.getPixel(height, photo?.stick_align).x;
          // 70 pixels is the determined leniency - incorrect once it's no longer on the right pole
          if (Math.abs(stickAlignPixelWidth - photoPixelWidth) <= 70 && heightPass) pass = true;
          [nodeUpdateInfo, certificationGrade] = trackGrade(pass, nodeUpdateInfo, nodeId, manualGradingFlag, certificationGrade);
          break;
        case 'Annotate the transformer at the correct place in the photo. You do not need to set a transformer spec or bearing.':
          const userTransformer = userMarkers.map((marker) => ({
            type: marker.measurement_of || marker.equipment_type,
            vert: marker._measured_height
          }));
          const keyTransformer = keyMarkers.map((marker) => ({
            type: marker.measurement_of || marker.equipment_type,
            vert: marker._measured_height
          }));

          pass =
            userTransformer.length >= 3 &&
            userTransformer.every((part, partIndex) => {
              const distance = Math.abs(keyTransformer[partIndex].vert - part.vert);
              if (distance <= 2.5) return true;
            });
          [nodeUpdateInfo, certificationGrade] = trackGrade(pass, nodeUpdateInfo, nodeId, manualGradingFlag, certificationGrade);
          break;
        case "Annotate the primaries at the correct place in the photo. You will need to annotate the arm and insulators and provide the correct specs (use 10' for the length of the arm).":
          const match =
            userMarkers.length > 0 &&
            userMarkers.every((marker) => {
              const parents = marker.parents;
              const insulator = parents.insulator;
              return (
                insulator &&
                parents.arm.arm_spec === "10' Double Alley Arm" &&
                insulator.insulator_spec === 'Pin (Crossarm)' &&
                marker._marker_context.type === 'wire' &&
                Math.abs(505 - marker._measured_height) <= 1.5 &&
                traces[marker._trace].cable_type === 'Primary' &&
                insulator.parents.arm.arm_spec === "10' Double Alley Arm"
              );
            });
          if (match && userMarkers?.length >= 3) pass = true;
          [nodeUpdateInfo, certificationGrade] = trackGrade(pass, nodeUpdateInfo, nodeId, manualGradingFlag, certificationGrade);
          break;
        case 'Use the midspan photo to enter the best over detail. Select the best input in the photo chip.':
          const midspanData = userPhotoDataObj[Object.keys(nodeData.photos)[0]]?.photofirst_data?.midspanHeight;
          const overDetail = midspanData ? Object.values(midspanData)[0].over : undefined;
          if (overDetail === 'Brush') pass = true;
          [nodeUpdateInfo, certificationGrade] = trackGrade(pass, nodeUpdateInfo, nodeId, manualGradingFlag, certificationGrade);
          break;
      }
    }
  }
  const certificationQuestionNum = questionNodes.length - manualGradingNumber;
  const gradePercentage = (certificationGrade / certificationQuestionNum) * 100;
  const certificationPass = gradePercentage == 100 ? true : false;

  // Add the needs_manual_grading style if certification has been passed
  let manualNodeSCIDS = '';
  if (certificationPass) {
    // Determine the scids that need manually graded and mark them + save their numbers
    manualNodeIds.forEach((id) => {
      nodeUpdateInfo[`${id}/attributes/needs_manual_grading/button_added`] = true;
      manualNodeSCIDS += PickAnAttribute(nodes[id].attributes, 'scid') + ' ';
    });

    // Put a flag in the database that says a user has passed autograding, so that they can't regrade and lower their attempts
    const updateObj = {};
    updateObj[certFinalizedAttributeName] = true;
    await FirebaseWorker.ref(`photoheight/company_space/${userGroup}/user_data/${uid}`).update(updateObj);
  }

  // Update node attribute information (incorrect questions and manual grading needed)
  await FirebaseWorker.ref(`photoheight/jobs/${jobId}/nodes`).update(nodeUpdateInfo);
  // Update num attempts remaining
  if (!certificationPass) {
    certAttemptsRemaining = certAttemptsRemaining - 1;
    const updateObj = {};
    updateObj.cert_attempts_remaining = certAttemptsRemaining;
    await FirebaseWorker.ref(`photoheight/jobs/${jobId}/metadata`).update(updateObj);
  }

  // Email data
  let subject;
  let body = `${userEmail} has submitted their certification job. They scored a ${certificationGrade + ' / ' + certificationQuestionNum}. `;
  if (certificationPass) {
    subject = 'Certification Autograding Pass';
    body += `Since they have passed the autograding, the followind SCID\'s need reviewed: ${manualNodeSCIDS}`;
  } else {
    if (certAttemptsRemaining === 0) {
      subject = 'Certification Autograding Fail';
      body = +'This was their final attempt, so they have now failed certification.';
    } else {
      subject = 'Certification Autograding Submission';
      body += `They didn\'t pass the autograding, and have ${certAttemptsRemaining} attempt`;
      if (certAttemptsRemaining > 1) body += 's remaining.';
      else if (certAttemptsRemaining === 1) body += ' remaining.';
    }
  }

  let toast = 'Katapult Engineering training staff have been notified of your submission';
  if (certificationPass && manualGradingNumber) toast += ' and will begin manual review';
  toast += '.';
  let sendToast = false;

  // Send the email to training
  const emailObject = {
    addresses: 'training@katapultengineering.com',
    subject,
    body,
    group_tag: 'internal'
  };
  const emailReqKey = FirebaseWorker.ref('/photoheight/server_requests/email/requests/').push(emailObject).key;
  FirebaseWorker.ref('/photoheight/server_requests/email/responses/' + emailReqKey).on(
    'value',
    function (snapshot) {
      var response = snapshot.val();
      if (response != null) {
        snapshot.ref.remove();
        sendToast = true;
      }
    }.bind(this)
  );

  if (!sendToast) toast = 'There has been an error in notifying Katapult Engineering Training.';

  return [
    certificationQuestionNum,
    certAttemptsRemaining,
    certificationPass,
    certificationGrade,
    gradePercentage,
    manualGradingNumber,
    toast
  ];
}

function getScids(scidsToMatch, nodes) {
  if (scidsToMatch.length > 1)
    return Object.entries(nodes).filter((node) => scidsToMatch.includes(PickAnAttribute(node[1].attributes, 'scid')));
  else return Object.entries(nodes).find((node) => scidsToMatch.includes(PickAnAttribute(node[1].attributes, 'scid'))) || [];
}

function getScidPhotoData(scid, userPhotoDataObj) {
  const photoKey = scid.photos ? Object.entries(scid.photos).find((photo) => photo[1].association === 'main')[0] : '';
  return Object.entries(userPhotoDataObj).find((photo) => photo[0] === photoKey) || [];
}

function getScidData(scid, userPhotoDataObj, traces) {
  const photoData = getScidPhotoData(scid, userPhotoDataObj);
  return DataViews.help.getProposedMarkers(photoData[1]?.photofirst_data, traces) || [];
}

function trackGrade(pass, nodeUpdateInfo, nodeId, manualGradingFlag, certificationGrade) {
  // Store question pass/fail data
  if (pass) {
    certificationGrade++;
    nodeUpdateInfo[`${nodeId}/attributes/incorrect_question/button_added`] = false;
  } else if (!manualGradingFlag) nodeUpdateInfo[`${nodeId}/attributes/incorrect_question/button_added`] = true;
  return [nodeUpdateInfo, certificationGrade];
}
